/**
 * ToyotaSalesUploader.jsx
 *
 * React upload component for the Toyota Sales Report API.
 * Supports up to 4 PDFs per session:
 *   daily_pdf      — Toyota Daily Sales Report (CY)   [required if no dealer_pdf]
 *   daily_pdf_py   — Toyota Daily Sales Report (PY)   [optional]
 *   dealer_pdf     — Dealer Sales Summary 11356 (CY)  [required if no daily_pdf]
 *   dealer_pdf_py  — Dealer Sales Summary 11356 (PY)  [optional]
 *
 * Produces:
 *   regional_sales_{date}.csv  → BigQuery table: regional_sales
 *   dealer_sales_{date}.csv    → BigQuery table: dealer_sales
 *
 * Props:
 *   apiBase {string} — Base URL of Express API. Defaults to http://localhost:3001
 */

import { useState, useRef, useCallback, useEffect } from "react";

const DEFAULT_API =
	process.env.REACT_APP_TOYOTA_API_URL || "http://localhost:3001";

// ── PDF slot definitions ───────────────────────────────────────────────────────

const PDF_SLOTS = [
	{
		key: "daily_pdf",
		label: "Daily Sales PDF — Current Year",
		sub: "Toyota Daily Sales Report (100-21637 / 21636 / 16436)",
		required: true,
		badge: "CY",
		badgeColor: "#cc0000"
	},
	{
		key: "dealer_pdf",
		label: "Dealer Summary PDF — Current Year",
		sub: "11356 Boston Region Dealer Sales Summary",
		required: false,
		badge: "CY",
		badgeColor: "#cc0000"
	},
	{
		key: "daily_pdf_py",
		label: "Daily Sales PDF — Prior Year",
		sub: "Same report format, prior year date — enables YoY analysis",
		required: false,
		badge: "PY",
		badgeColor: "#888"
	},
	{
		key: "dealer_pdf_py",
		label: "Dealer Summary PDF — Prior Year",
		sub: "Prior year 11356 — enables dealer YoY comparisons",
		required: false,
		badge: "PY",
		badgeColor: "#888"
	}
];

// ── Sub-components ─────────────────────────────────────────────────────────────

function DropSlot({ slot, file, onFile, onClear }) {
	const [drag, setDrag] = useState(false);
	const inputRef = useRef(null);

	const accept = (f) => {
		if (!f) return;
		if (!f.name.toLowerCase().endsWith(".pdf")) return;
		onFile(f);
	};

	return (
		<div
			style={{
				...S.slot,
				borderColor: drag ? "#cc0000" : file ? "#1a7a3a" : "#ddd",
				background: drag ? "#fff8f8" : file ? "#f5fff8" : "#fafafa",
				cursor: "pointer"
			}}
			onDragOver={(e) => {
				e.preventDefault();
				setDrag(true);
			}}
			onDragLeave={() => setDrag(false)}
			onDrop={(e) => {
				e.preventDefault();
				setDrag(false);
				accept(e.dataTransfer.files[0]);
			}}
			onClick={() => !file && inputRef.current?.click()}
		>
			<input
				ref={inputRef}
				type="file"
				accept=".pdf"
				style={{ display: "none" }}
				onChange={(e) => accept(e.target.files[0])}
			/>

			{/* Badge */}
			<span style={{ ...S.badge, background: slot.badgeColor }}>
				{slot.badge}
			</span>

			{/* Labels */}
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={S.slotLabel}>
					{slot.label}
					{slot.required && <span style={S.reqDot}> *</span>}
				</div>
				<div style={S.slotSub}>{slot.sub}</div>
				{file && <div style={S.slotFile}>✓ {file.name}</div>}
			</div>

			{/* Action */}
			{file ? (
				<button
					style={S.clearBtn}
					onClick={(e) => {
						e.stopPropagation();
						onClear();
					}}
				>
					✕
				</button>
			) : (
				<span style={S.slotAdd}>+ Add</span>
			)}
		</div>
	);
}

function VerificationPanel({ label, verification, defaultOpen = true }) {
	const [open, setOpen] = useState(defaultOpen);
	if (!verification) return null;

	const { passed, checks } = verification;
	const passCount = checks.filter((c) => c.passed).length;
	const borderColor = passed ? "#1a7a3a" : "#cc0000";
	const bgColor = passed ? "#f5fff8" : "#fff5f5";
	const headColor = passed ? "#0f6e56" : "#990000";

	return (
		<div
			style={{
				...S.verifyPanel,
				border: `1px solid ${borderColor}`,
				background: bgColor
			}}
		>
			<button
				style={{
					...S.verifyHeader,
					borderBottom: open ? `1px solid ${borderColor}` : "none"
				}}
				onClick={() => setOpen((o) => !o)}
			>
				<span style={{ ...S.verifyTitle, color: headColor }}>
					{passed ? "✓" : "✗"} {label}
				</span>
				<span style={S.verifyCount}>
					{passCount}/{checks.length} checks &nbsp; {open ? "▲" : "▼"}
				</span>
			</button>

			{open &&
				checks.map((check, i) => (
					<div
						key={i}
						style={{
							...S.verifyCheck,
							background: check.passed ? "transparent" : "#fff8f8",
							borderBottom:
								i < checks.length - 1 ? `1px solid ${borderColor}20` : "none"
						}}
					>
						<div
							style={{
								color: check.passed ? "#0f6e56" : "#990000",
								...S.mono,
								fontSize: 11
							}}
						>
							{check.passed ? "✓" : "✗"} {check.name}
						</div>
						{check.spot && (
							<div style={S.subList}>
								{check.spot.map((s, j) => (
									<div
										key={j}
										style={{
											color: s.ok ? "#666" : "#990000",
											...S.mono,
											fontSize: 10,
											paddingLeft: 16
										}}
									>
										{s.ok ? "·" : "✗"} {s.series} &nbsp; MTD={s.mtd} &nbsp;
										<span style={{ color: "#aaa" }}>{s.note}</span>
									</div>
								))}
							</div>
						)}
						{!check.passed && check.details?.length > 0 && (
							<div style={S.subList}>
								{check.details.map((d, j) => (
									<div
										key={j}
										style={{
											color: "#990000",
											...S.mono,
											fontSize: 10,
											paddingLeft: 16
										}}
									>
										{d}
									</div>
								))}
								{check.truncated && (
									<div
										style={{
											color: "#aaa",
											...S.mono,
											fontSize: 10,
											paddingLeft: 16
										}}
									>
										… and more
									</div>
								)}
							</div>
						)}
					</div>
				))}
		</div>
	);
}

function FileRow({ file, sessionId, apiBase }) {
	const [busy, setBusy] = useState(false);

	const download = async () => {
		setBusy(true);
		try {
			const res = await fetch(
				`${apiBase}/api/download/${sessionId}/${file.key}`
			);
			if (!res.ok) throw new Error("Download failed");
			const blob = await res.blob();
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = file.name;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (e) {
			alert(`Download error: ${e.message}`);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div style={S.fileRow}>
			<div>
				<div style={{ ...S.mono, fontSize: 11, fontWeight: 600 }}>
					{file.name}
				</div>
				<div style={{ ...S.mono, fontSize: 10, color: "#999", marginTop: 2 }}>
					BigQuery → {file.table} &nbsp;·&nbsp; {file.rows.toLocaleString()}{" "}
					rows
				</div>
			</div>
			<button
				onClick={download}
				disabled={busy}
				style={{ ...S.dlBtn, opacity: busy ? 0.5 : 1 }}
			>
				{busy ? "…" : "↓ CSV"}
			</button>
		</div>
	);
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function ToyotaSalesUploader({
	mode = "aiplatform",
	apiBase = DEFAULT_API
}) {
	// One file state per slot
	const [files, setFiles] = useState({}); // { slot_key: File }
	const [processing, setProcessing] = useState(false);
	const [progressMsg, setProgressMsg] = useState("");
	const [error, setError] = useState("");
	const [result, setResult] = useState(null); // API response
	const [gcsStatus, setGcsStatus] = useState(null); // null|'uploading'|{success}|{error}

	useEffect(() => {
		if (!mode) return;
		const url = new URL(window.location.href);
		if (url.searchParams.get("mode") === mode) return;
		url.searchParams.set("mode", mode);
		window.history.replaceState({}, "", `${url.pathname}${url.search}`);
	}, [mode]);

	const setFile = (key, file) => setFiles((f) => ({ ...f, [key]: file }));
	const clearFile = (key) =>
		setFiles((f) => {
			const n = { ...f };
			delete n[key];
			return n;
		});

	const hasDaily = !!files.daily_pdf;
	const hasDealer = !!files.dealer_pdf;
	const canSubmit = (hasDaily || hasDealer) && !processing;

	const reset = () => {
		setFiles({});
		setError("");
		setResult(null);
		setGcsStatus(null);
		setProgressMsg("");
	};

	const handleProcess = async () => {
		if (!canSubmit) return;
		setProcessing(true);
		setError("");
		setResult(null);
		setGcsStatus(null);

		const formData = new FormData();
		for (const [key, file] of Object.entries(files)) {
			formData.append(key, file);
		}

		try {
			setProgressMsg("Uploading PDFs…");
			const res = await fetch(`${apiBase}/api/process`, {
				method: "POST",
				body: formData
			});
			setProgressMsg("Processing and verifying…");
			const data = await res.json();
			if (!res.ok || data.error)
				throw new Error(data.error || "Processing failed");
			setResult(data);
		} catch (err) {
			setError(err.message || "Unexpected error");
		} finally {
			setProcessing(false);
			setProgressMsg("");
		}
	};

	const handleGcs = async () => {
		if (!result) return;
		setGcsStatus("uploading");
		try {
			const res = await fetch(`${apiBase}/api/upload-gcs/${result.sessionId}`, {
				method: "POST"
			});
			const data = await res.json();
			if (!res.ok || data.error)
				throw new Error(data.error || "GCS upload failed");
			setGcsStatus({ success: true, files: data.files });
		} catch (err) {
			setGcsStatus({ error: err.message });
		}
	};

	// Both verifications must pass to enable GCS upload
	const dailyPassed = result?.dailyVerification?.passed ?? true;
	const dealerPassed = result?.dealerVerification?.passed ?? true;
	const allPassed = dailyPassed && dealerPassed;

	return (
		<div id="htmlPage">
			<h1>Toyota Sales Uploader</h1>
			<div style={S.card}>
				<div style={S.cardTag}>BigQuery CSV Export</div>
				<h1 style={S.h1}>
					Toyota PDF
					<br />
					<strong>→ Structured Data</strong>
				</h1>
				<p style={S.subtitle}>
					Upload up to 4 PDFs to produce two BigQuery-ready CSV files. At
					minimum, upload a Current Year daily PDF or dealer PDF. Add Prior Year
					files to enable YoY analysis.
				</p>

				{/* ── PDF Slots ── */}
				<div style={S.slotsGrid}>
					{PDF_SLOTS.map((slot) => (
						<DropSlot
							key={slot.key}
							slot={slot}
							file={files[slot.key] || null}
							onFile={(f) => setFile(slot.key, f)}
							onClear={() => clearFile(slot.key)}
						/>
					))}
				</div>

				{/* ── File count summary ── */}
				<div style={S.slotSummary}>
					{Object.keys(files).length === 0
						? "No files added yet — drop PDFs into the slots above"
						: `${Object.keys(files).length} of 4 files added · ${Object.keys(files).join(", ")}`}
				</div>

				{/* ── Process button ── */}
				<button
					style={{
						...S.processBtn,
						background: canSubmit ? "#1a1a1a" : "#bbb",
						cursor: canSubmit ? "pointer" : "not-allowed"
					}}
					disabled={!canSubmit}
					onClick={handleProcess}
				>
					{processing ? progressMsg || "Processing…" : "Process Reports"}
				</button>

				{/* ── Error ── */}
				{error && <div style={S.errorBox}>⚠ {error}</div>}

				{/* ── Results ── */}
				{result && (
					<div style={S.results}>
						<div
							style={{
								...S.resultTag,
								color: allPassed ? "#0f6e56" : "#cc0000"
							}}
						>
							{allPassed
								? "✓ All checks passed"
								: "⚠ Review verification issues before uploading"}
						</div>

						{/* Stats grid */}
						<div style={S.statsGrid}>
							{[
								{ label: "Report Date", val: result.stats.reportDate },
								{
									label: "Series Parsed",
									val: result.stats.seriesCount || "—"
								},
								{
									label: "Regional Rows",
									val:
										(
											result.files.find((f) => f.key === "regional")?.rows ??
											"—"
										).toLocaleString?.() ?? "—"
								},
								{
									label: "Dealer Rows",
									val:
										(
											result.files.find((f) => f.key === "dealer")?.rows ?? "—"
										).toLocaleString?.() ?? "—"
								},
								{
									label: "Dealers Found",
									val: result.stats.dealerCount || "—"
								},
								{
									label: "Prior Year",
									val: result.stats.hasPriorYear ? "Yes" : "No"
								}
							].map(({ label, val }) => (
								<div key={label} style={S.statCell}>
									<div style={S.statVal}>{val}</div>
									<div style={S.statKey}>{label}</div>
								</div>
							))}
						</div>

						{/* Verification panels */}
						{result.dailyVerification && (
							<VerificationPanel
								label="Daily PDF verification"
								verification={result.dailyVerification}
								defaultOpen={!result.dailyVerification.passed}
							/>
						)}
						{result.dealerVerification && (
							<VerificationPanel
								label="Dealer PDF verification"
								verification={result.dealerVerification}
								defaultOpen={!result.dealerVerification.passed}
							/>
						)}

						{/* File downloads */}
						<div style={S.fileList}>
							{result.files.map((f) => (
								<FileRow
									key={f.key}
									file={f}
									sessionId={result.sessionId}
									apiBase={apiBase}
								/>
							))}
						</div>

						{/* GCS upload */}
						<div style={S.divider} />

						{gcsStatus === null && (
							<button
								style={{
									...S.gcsBtn,
									background: allPassed ? "#cc0000" : "#bbb",
									cursor: allPassed ? "pointer" : "not-allowed"
								}}
								disabled={!allPassed}
								onClick={handleGcs}
								title={
									allPassed
										? ""
										: "Fix verification issues before uploading to GCS"
								}
							>
								↑ Upload to Google Cloud Storage
							</button>
						)}

						{gcsStatus === "uploading" && (
							<div
								style={{
									...S.mono,
									fontSize: 12,
									color: "#555",
									textAlign: "center",
									padding: 12
								}}
							>
								Uploading to GCS…
							</div>
						)}

						{gcsStatus?.success && (
							<div
								style={{
									...S.gcsResult,
									borderColor: "#1a7a3a",
									background: "#f5fff8"
								}}
							>
								<div
									style={{
										color: "#0f6e56",
										fontWeight: 600,
										marginBottom: 8
									}}
								>
									✓ Uploaded to Google Cloud Storage
								</div>
								{gcsStatus.files.map((f) => (
									<div
										key={f.uri}
										style={{
											...S.mono,
											fontSize: 10,
											color: "#444",
											marginBottom: 4
										}}
									>
										<span style={{ color: "#999" }}>{f.table}</span> → {f.uri}
									</div>
								))}
							</div>
						)}

						{gcsStatus?.error && (
							<div
								style={{
									...S.gcsResult,
									borderColor: "#cc0000",
									background: "#fff5f5"
								}}
							>
								<div style={{ color: "#990000", ...S.mono, fontSize: 12 }}>
									✗ GCS upload failed: {gcsStatus.error}
								</div>
							</div>
						)}

						<button onClick={reset} style={S.resetBtn}>
							Process another batch
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const IBM = "'IBM Plex Sans', system-ui, sans-serif";
const S = {
	card: {
		background: "#fff",
		border: "1px solid #e0e0e0",
		maxWidth: 660,
		padding: "48px 48px 40px",
		margin: "0 auto"
	},
	cardTag: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: "0.15em",
		textTransform: "uppercase",
		color: "#cc0000",
		marginBottom: 8
	},
	h1: {
		fontSize: 26,
		fontWeight: 300,
		lineHeight: 1.2,
		marginBottom: 8,
		letterSpacing: "-0.3px"
	},
	subtitle: { fontSize: 13, color: "#666", lineHeight: 1.65, marginBottom: 32 },
	slotsGrid: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		marginBottom: 12
	},
	slot: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		padding: "14px 16px",
		border: "1.5px dashed",
		transition: "border-color 0.15s, background 0.15s",
		userSelect: "none"
	},
	badge: {
		flexShrink: 0,
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 9,
		fontWeight: 700,
		letterSpacing: "0.1em",
		color: "#fff",
		padding: "2px 6px",
		borderRadius: 2
	},
	slotLabel: { fontSize: 12, fontWeight: 600, marginBottom: 2 },
	reqDot: { color: "#cc0000" },
	slotSub: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		color: "#999"
	},
	slotFile: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		color: "#1a7a3a",
		marginTop: 4,
		fontWeight: 600
	},
	slotAdd: {
		flexShrink: 0,
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		color: "#aaa",
		fontWeight: 600
	},
	clearBtn: {
		flexShrink: 0,
		background: "none",
		border: "none",
		color: "#aaa",
		fontSize: 14,
		cursor: "pointer",
		padding: "0 4px",
		lineHeight: 1
	},
	slotSummary: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		color: "#aaa",
		marginBottom: 20,
		minHeight: 16
	},
	processBtn: {
		display: "block",
		width: "100%",
		color: "#fff",
		border: "none",
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		padding: 16,
		transition: "background 0.15s"
	},
	errorBox: {
		marginTop: 20,
		background: "#fff5f5",
		border: "1px solid #ffcccc",
		borderLeft: "3px solid #cc0000",
		padding: "14px 18px",
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 12,
		color: "#990000"
	},
	results: { marginTop: 32, borderTop: "1px solid #e0e0e0", paddingTop: 28 },
	resultTag: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		fontWeight: 700,
		letterSpacing: "0.15em",
		textTransform: "uppercase",
		marginBottom: 20
	},
	statsGrid: {
		display: "grid",
		gridTemplateColumns: "repeat(3, 1fr)",
		gap: 1,
		background: "#e0e0e0",
		border: "1px solid #e0e0e0",
		marginBottom: 20
	},
	statCell: { background: "#fff", padding: "14px 16px" },
	statVal: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 20,
		fontWeight: 700,
		lineHeight: 1,
		marginBottom: 4
	},
	statKey: {
		fontSize: 10,
		color: "#aaa",
		textTransform: "uppercase",
		letterSpacing: "0.05em"
	},
	verifyPanel: { marginBottom: 12, overflow: "hidden" },
	verifyHeader: {
		width: "100%",
		display: "flex",
		alignItems: "center",
		background: "none",
		border: "none",
		borderBottom: "1px solid",
		padding: "10px 14px",
		cursor: "pointer",
		textAlign: "left"
	},
	verifyTitle: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 12,
		fontWeight: 600
	},
	verifyCount: {
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 10,
		color: "#aaa",
		marginLeft: "auto"
	},
	verifyCheck: { padding: "8px 14px" },
	subList: { marginTop: 4, display: "flex", flexDirection: "column", gap: 2 },
	fileList: {
		display: "flex",
		flexDirection: "column",
		gap: 8,
		marginBottom: 20
	},
	fileRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: "12px 14px",
		background: "#f7f7f5",
		border: "1px solid #e0e0e0"
	},
	dlBtn: {
		background: "transparent",
		border: "1px solid #ccc",
		color: "#333",
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 11,
		fontWeight: 600,
		padding: "6px 12px",
		cursor: "pointer",
		flexShrink: 0,
		letterSpacing: "0.05em"
	},
	divider: { borderTop: "1px solid #e0e0e0", marginBottom: 18 },
	gcsBtn: {
		display: "block",
		width: "100%",
		color: "#fff",
		border: "none",
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 12,
		fontWeight: 700,
		letterSpacing: "0.1em",
		textTransform: "uppercase",
		padding: 16,
		marginBottom: 12,
		transition: "background 0.15s"
	},
	gcsResult: { border: "1px solid", padding: "12px 14px", marginBottom: 12 },
	resetBtn: {
		display: "block",
		width: "100%",
		background: "transparent",
		border: "1px solid #ddd",
		color: "#888",
		fontFamily: "'IBM Plex Mono', monospace",
		fontSize: 11,
		padding: 12,
		cursor: "pointer",
		marginTop: 12
	},
	mono: { fontFamily: "'IBM Plex Mono', monospace" }
};
